import type { Clerk } from '@clerk/clerk-js';

/**
 * Layer 2 in the browser.
 *
 * Three rules from `double-layer-auth`, and every one of them is a thing that goes wrong when it
 * is not written down:
 *
 *   1. **`getToken()` with no arguments.** No template name, no `VITE_CLERK_JWT_TEMPLATE`. A
 *      template token passes every signature check and has no `sid`, so the API refuses it — and
 *      the failure looks like "auth is broken" rather than "we asked for the wrong token".
 *   2. **Never `signOut()` on a Layer-1 refusal.** The Clerk session is fleet-wide. Signing
 *      somebody out of every leychoon.app app because they lack a role *here* is a worse outcome
 *      than the missing role, and the person it happens to is precisely the colleague who clicked
 *      through from Home.
 *   3. **A fresh token per request.** Clerk's session tokens are short-lived by design;
 *      `getToken()` refreshes when needed, so it is called on every call rather than cached.
 *
 * The publishable key is **build-time** (`VITE_CLERK_PUBLISHABLE_KEY`): it is compiled into the
 * bundle, so a runtime-only variable in Coolify does nothing at all. A build without it produces
 * an app that says so on screen instead of showing a sign-in form that cannot work.
 *
 * The SDK is **loaded on demand**, not imported at the top level. Measured: a static import put
 * 700 kB gzipped into the main chunk — more than doubling it — and this app is opened on a phone
 * on a site by managers between jobs. Now the first paint costs nothing for it, and the chunk
 * arrives when there is actually a session to establish.
 */

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

export const clerkConfigured = typeof PUBLISHABLE_KEY === 'string' && PUBLISHABLE_KEY !== '';

let instance: Clerk | null = null;
let loading: Promise<Clerk | null> | null = null;
let unreachable = false;
let failure: string | null = null;

/**
 * How long to wait for the whole load before giving up.
 *
 * Found the hard way, twice. First: with `clerk.leychoon.app` unreachable, `clerk.load()` never
 * settles, so the first `GET /me` never fires and the app sits on "Loading the app…" for ever.
 * Then: the deadline only covered `clerk.load()` — the dynamic `import()` and the `new Clerk()`
 * before it were unbounded, so anything that stalled *earlier* still hung for ever. The whole
 * sequence is inside the deadline now.
 *
 * Ten seconds: a cold chunk fetch plus a handshake on a phone on a site can genuinely take a
 * while, and nothing waits on this any more — `sessionToken()` returning null just means a 401,
 * which the gate already renders.
 */
const LOAD_TIMEOUT_MS = 10_000;

/** True once a load attempt has failed or timed out. Drives the "sign-in unavailable" state. */
export function clerkUnreachable(): boolean {
  return unreachable;
}

/**
 * Why the last attempt failed, in Clerk's own words.
 *
 * Worth keeping and worth showing. "Did not answer" covers a network failure, but the same code
 * path catches a *configuration* refusal — an origin the instance does not recognise, a satellite
 * domain with no primary — and those need a completely different fix. Without the message on
 * screen, telling them apart takes a round trip to a browser console.
 */
export function clerkFailure(): string | null {
  return failure;
}

/**
 * Run `work` against a deadline.
 *
 * `work` is *invoked inside* the race rather than before it, which is the entire point: an
 * `await import()` that never settles has to be covered too.
 */
async function withDeadline<T>(work: () => Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      work(),
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(
          () => reject(new Error(`the sign-in service did not respond within ${ms / 1_000}s`)),
          ms,
        );
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Load Clerk once.
 *
 * `load()` is idempotent in Clerk's own SDK, but the promise is cached anyway: React renders more
 * than once, and two concurrent `load()` calls on a slow connection is a race nobody needs.
 */
export function loadClerk(): Promise<Clerk | null> {
  if (!clerkConfigured) return Promise.resolve(null);
  if (instance) return Promise.resolve(instance);
  loading ??= (async () => {
    try {
      const clerk = await withDeadline(async () => {
        const { Clerk } = await import('@clerk/clerk-js');
        const created = new Clerk(PUBLISHABLE_KEY as string);
        // No redirects: this app is one origin and the sign-in is mounted in place, so a
        // redirect URL would only be a way to end up somewhere unexpected.
        await created.load({ standardBrowser: true });
        return created;
      }, LOAD_TIMEOUT_MS);

      instance = clerk;
      unreachable = false;
      failure = null;
      return clerk;
    } catch (error) {
      // Left recoverable on purpose: `retryClerk()` clears the cached promise so a Retry button
      // means something. Returning null rather than throwing keeps every caller simple — no
      // session is a state the gate already knows how to render. The message is kept because it
      // is the only thing that separates "the network is down" from "this instance refuses this
      // origin", and those have different fixes.
      unreachable = true;
      failure = error instanceof Error ? error.message : String(error);
      return null;
    }
  })();
  return loading;
}

export function clerkInstance(): Clerk | null {
  return instance;
}

/** Forget a failed attempt, so a Retry button can try again for real. */
export function retryClerk(): Promise<Clerk | null> {
  loading = null;
  unreachable = false;
  failure = null;
  return loadClerk();
}

/**
 * The Bearer token for one API call, or null.
 *
 * Called with no arguments, deliberately — see rule 1. Returning null rather than throwing keeps
 * the caller simple: no session means no header, and the API answers 401, which the gate already
 * knows how to render.
 *
 * On a build with no publishable key this returns immediately without loading anything, so the
 * dev-stub path never pays for the SDK.
 */
export async function sessionToken(): Promise<string | null> {
  if (!clerkConfigured) return null;
  const clerk = instance ?? (await loadClerk());
  if (!clerk?.session) return null;
  try {
    return await clerk.session.getToken();
  } catch {
    // A refresh that fails mid-session is a 401 waiting to happen; let it be one.
    return null;
  }
}

/** Whether somebody is signed in at all — Layer 2, before Layer 1 has been asked. */
export function hasClerkSession(): boolean {
  return Boolean(instance?.session);
}

export function clerkEmail(): string | null {
  return instance?.user?.primaryEmailAddress?.emailAddress ?? null;
}

/**
 * Sign out — only ever from a button somebody pressed.
 *
 * It ends the session across the whole fleet, which is the right behaviour for a deliberate
 * "sign out" and the wrong behaviour for everything else.
 */
export async function signOutEverywhere(): Promise<void> {
  await instance?.signOut();
}
